import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { login } from "../api/auth";
import { getProfile } from "../api/agents";
import { useAuthStore } from "../store/authStore";
import { Button, Input, Label } from "../components/common/UIKit";
import { Database, Eye, EyeOff } from "lucide-react";
import { useSiteName } from "../hooks/useSiteName";
import { errMsg } from "../lib/utils";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/cf0c5174-481d-49d7-b7d1-7024e4fe2710/images/3ddb46e773450f7290cab9edd9887d1087e0794400b1640dcf790bccc2b2db9f.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const siteName = useSiteName();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const tokenRes = await login({ email, password });
      useAuthStore.setState({ token: tokenRes.access_token });
      const me = await getProfile();
      setAuth({ token: tokenRes.access_token, user: me });
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex relative bg-zinc-950 overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/80 via-zinc-900/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
         <div className="flex items-center gap-2 text-white">
      <Database className="h-5 w-5" strokeWidth={2.5} />
      <span
        onClick={() => navigate('/')}
        className="text-base font-black tracking-tight cursor-pointer"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        data-testid="login-site-name"
      >
        {siteName}
      </span>
</div>
          <div className="text-white max-w-md">
            <h2 className="text-5xl font-black tracking-tight leading-none mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              The control room for real estate leads.
            </h2>
            <p className="text-zinc-300 text-sm leading-relaxed">
              Subscribe to counties. Target lead types. Close faster with a live pipeline — FSBO, NOD, Probate, Tax Liens, and more.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="text-4xl font-black tracking-tight text-zinc-950 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Sign in
          </h1>
          <p className="text-sm text-zinc-500 mb-8">Access your leads and subscriptions.</p>

          <div className="mb-4">
            <Label>Email</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@agency.com"
              required
            />
          </div>
          <div className="mb-6">
            <Label>Password</Label>
            <div className="relative">
              <Input
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="pr-10"
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-950"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button data-testid="login-submit-button" type="submit" className="w-full" disabled={loading}>
            {loading ? "Signing in..." : "Sign in"}
          </Button>
          <p className="text-sm text-zinc-500 mt-6 text-center">
            No account?{" "}
            <Link to="/signup" className="font-semibold text-zinc-950 hover:underline" data-testid="go-to-signup-link">
              Create one
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
